<template>
    <div class="layout-index-footer">
        <div class="layout-index-footer__panel">
            <div class="layout-index-footer__links">
                <div class="layout-index-footer__mobile-brand">
                    <img
                        :src="footerLogoSrc"
                        alt="健豪數位網"
                        class="layout-index-footer__logo"
                        width="464"
                        height="104"
                    />
                    <div class="layout-index-footer__socials">
                        <a
                            v-for="social in footerSocialLinks"
                            :key="`mobile-${social.label}`"
                            :href="social.url"
                            :aria-label="social.label"
                            class="layout-index-footer__social-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <svg-icon
                                :icon-name="social.iconName"
                                class="layout-index-footer__social-icon"
                            ></svg-icon>
                        </a>
                    </div>
                </div>

                <section
                    v-for="section in footerSections"
                    :key="section.key"
                    class="footer-group"
                >
                    <h6
                        class="footer-group__title"
                        :class="{ 'is-open': activeSectionKey === section.key }"
                        @click="onTitleClick(section.key)"
                    >
                        {{ section.title }}
                        <svg-icon
                            icon-name="arrow-down-small-regular"
                            class="footer-group__title-arrow"
                        ></svg-icon>
                    </h6>
                    <div
                        :ref="setContentRef(section.key)"
                        class="footer-group__content"
                    >
                        <ul class="footer-group__list">
                            <li
                                v-for="link in section.links"
                                :key="`${section.key}-${link.label}`"
                                class="footer-group__item"
                            >
                                <a
                                    :href="link.url"
                                    :target="
                                        link.isExternal ? '_blank' : undefined
                                    "
                                    :rel="
                                        link.isExternal
                                            ? 'noopener noreferrer'
                                            : undefined
                                    "
                                >
                                    {{ link.label }}
                                </a>
                            </li>
                        </ul>
                    </div>
                </section>

                <section class="footer-group footer-group--stores">
                    <h6
                        class="footer-group__title"
                        :class="{
                            'is-open': activeSectionKey === storeSectionKey,
                        }"
                        @click="onTitleClick(storeSectionKey)"
                    >
                        服務據點
                        <svg-icon
                            icon-name="arrow-down-small-regular"
                            class="footer-group__title-arrow"
                        ></svg-icon>
                    </h6>
                    <div
                        :ref="setContentRef(storeSectionKey)"
                        class="footer-group__content"
                    >
                        <footer-store-qr
                            :stores="footerStores"
                        ></footer-store-qr>
                    </div>
                </section>
            </div>

            <div class="layout-index-footer__bottom">
                <div class="layout-index-footer__brand">
                    <a href="/" class="layout-index-footer__logo-link">
                        <img
                            :src="footerLogoSrc"
                            alt="健豪數位網"
                            class="layout-index-footer__logo"
                            width="464"
                            height="104"
                        />
                    </a>
                    <div class="layout-index-footer__socials">
                        <a
                            v-for="social in footerSocialLinks"
                            :key="social.label"
                            :href="social.url"
                            :aria-label="social.label"
                            class="layout-index-footer__social-link"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <svg-icon
                                :icon-name="social.iconName"
                                class="layout-index-footer__social-icon"
                            ></svg-icon>
                        </a>
                    </div>
                </div>

                <div class="layout-index-footer__terms">
                    <a
                        v-for="term in footerTermLinks"
                        :key="term.label"
                        :href="term.url"
                        class="layout-index-footer__term-link"
                    >
                        {{ term.label }}
                    </a>
                    <span class="layout-index-footer__copyright">
                        {{ footerCopyright }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { onMounted, onUnmounted, ref, watch } from 'vue';
    import FooterStoreQr from '@/components/layout/FooterStoreQr.vue';
    import SvgIcon from '@/components/base/SvgIcon.vue';
    import {
        LAYOUT_FOOTER_COPYRIGHT,
        LAYOUT_FOOTER_LINK_GROUPS,
        LAYOUT_FOOTER_SOCIAL_LINKS,
        LAYOUT_FOOTER_STORES,
        LAYOUT_FOOTER_TERM_LINKS,
        footerLogoSrc,
    } from '@/components/layout/constants/LayoutFooter.constants';

    // ----- state -----
    const storeSectionKey = 'stores';
    const footerSections = LAYOUT_FOOTER_LINK_GROUPS;
    const footerStores = LAYOUT_FOOTER_STORES;
    const footerSocialLinks = LAYOUT_FOOTER_SOCIAL_LINKS;
    const footerTermLinks = LAYOUT_FOOTER_TERM_LINKS;
    const footerCopyright = LAYOUT_FOOTER_COPYRIGHT;
    const mobileQuery = window.matchMedia('(max-width: 992px)');

    // 行動版 accordion
    const activeSectionKey = ref<string | null>(null);
    const contentRefs: Record<string, HTMLElement> = {};
    const setContentRef =
        (key: string) =>
        (el: unknown): void => {
            if (el instanceof HTMLElement) contentRefs[key] = el;
        };

    // ----- methods -----
    const onTitleClick = (key: string): void => {
        activeSectionKey.value = activeSectionKey.value === key ? null : key;
    };

    const slideDown = (el: HTMLElement): void => {
        el.style.display = 'block';
        const targetHeight = el.scrollHeight;
        el.style.height = '0';
        el.style.overflow = 'hidden';
        void el.offsetHeight; // 觸發 reflow，確保 height:0 先渲染再啟動 transition
        el.style.transition = 'height 0.35s ease';
        el.style.height = `${targetHeight}px`;
        el.addEventListener(
            'transitionend',
            () => {
                el.style.height = '';
                el.style.overflow = '';
                el.style.transition = '';
            },
            { once: true }
        );
    };

    const slideUp = (el: HTMLElement): void => {
        el.style.height = `${el.scrollHeight}px`;
        el.style.overflow = 'hidden';
        void el.offsetHeight;
        el.style.transition = 'height 0.35s ease';
        el.style.height = '0';
        el.addEventListener(
            'transitionend',
            () => {
                el.style.display = 'none';
                el.style.height = '';
                el.style.overflow = '';
                el.style.transition = '';
            },
            { once: true }
        );
    };

    // 切換到桌機時清除殘留 inline style，避免 display:none 持續生效
    const onQueryChange = (e: MediaQueryListEvent): void => {
        if (!e.matches) {
            Object.values(contentRefs).forEach((el) => {
                el.style.display = '';
                el.style.height = '';
                el.style.overflow = '';
                el.style.transition = '';
            });
            activeSectionKey.value = null;
        }
    };

    // ----- watch -----
    watch(activeSectionKey, (newKey, oldKey) => {
        if (!mobileQuery.matches) return;
        if (oldKey && contentRefs[oldKey]) slideUp(contentRefs[oldKey]);
        if (newKey && contentRefs[newKey]) slideDown(contentRefs[newKey]);
    });

    // ----- lifecycle -----
    onMounted(() => {
        mobileQuery.addEventListener('change', onQueryChange);
    });

    onUnmounted(() => mobileQuery.removeEventListener('change', onQueryChange));
</script>

<style scoped lang="scss">
    .layout-index-footer {
        padding: 0 24px 3.5rem;
        font-size: 1rem;

        a {
            color: inherit;
            text-decoration: none;
        }

        &__panel {
            max-width: 1200px;
            margin: 0 auto;
        }

        &__mobile-brand {
            display: none;
        }

        &__links {
            width: 100%;
            padding: 3.5rem 0 2rem;
            display: flex;
            justify-content: space-between;
        }

        &__bottom {
            width: 100%;
            padding: 0.5rem 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-top: 1px solid #7a7a7a;
        }

        &__brand {
            display: flex;
            gap: 16px;
            align-items: center;
        }

        &__logo-link {
            display: inline-flex;
        }

        &__logo {
            width: 100%;
            max-width: 232px;
            height: auto;
            display: block;
        }

        &__socials {
            display: flex;
            gap: 16px;
            align-items: center;
        }

        &__social-link {
            display: inline-flex;
            line-height: 0;
        }

        &__social-icon {
            width: 24px;
            height: 24px;
            display: block !important;
        }

        &__terms {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            align-items: center;
            justify-content: flex-end;
            font-size: 1rem;
            line-height: 1.5;
        }

        &__copyright {
            color: inherit;
        }
    }

    .footer-group {
        flex: 0 0 15%;
        display: flex;
        flex-direction: column;

        &--stores {
            flex-basis: 40%;
        }

        &__title {
            width: 100%;
            padding: 0;
            margin: 0 0 16px;
            display: flex;
            align-items: center;
            color: inherit;
            font-size: 1.25rem;
            font-weight: 700;
            line-height: 1.4;
            text-align: left;
        }

        &__title-arrow {
            display: none !important;
        }

        &__list {
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 0;
            list-style: none;
        }

        &__item {
            color: inherit;
            font-size: 1rem;
            line-height: 2.2;
        }
    }

    @media (max-width: 992px) {
        .layout-index-footer {
            padding: 0;

            &__panel {
                max-width: none;
            }

            &__links {
                padding: 0;
                display: block;
            }

            &__mobile-brand {
                width: 100%;
                padding: 32px 16px;
                display: block;
                justify-items: center;
                border-bottom: 1px solid $footerBorderColor;

                .layout-index-footer__socials {
                    margin-top: 24px;
                    margin-left: 0;
                    justify-content: center;
                }
            }

            &__bottom {
                padding: 16px 0 24px;
                margin-bottom: 0;
                justify-content: center;
                border-top: 0;
            }

            &__terms {
                width: 100%;
                padding: 0 16px;
                gap: 8px 16px;
                justify-content: center;
                text-align: center;
            }

            &__term-link {
                margin-right: 0;
            }

            &__copyright {
                width: 100%;
                display: block;
            }

            &__brand {
                display: none;
            }
        }

        .footer-group {
            flex-basis: 100%;
            border-bottom: 1px solid $footerBorderColor;

            &--stores {
                flex-basis: 100%;
            }

            &__title {
                margin-bottom: 0;
                padding: 1.125rem 1.5rem;
                justify-content: space-between;
                font-size: 1.25rem;
                cursor: pointer;
            }

            &__title-arrow {
                flex-shrink: 0;
                width: 20px;
                height: 20px;
                display: block !important;
                transition: transform 0.3s;
            }

            &__title.is-open &__title-arrow {
                transform: rotate(180deg);
            }

            &__item {
                font-size: 1.125rem;
            }

            // 初始收合，由 JS slideDown/slideUp 控制動畫
            .footer-group__content {
                display: none;
            }
        }

        .footer-group__list {
            padding: 0 24px 16px;
        }

        .footer-group__content {
            min-width: 0;
        }
    }
</style>
